import React from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import "containers/common/List.css";
import "containers/common/Table.css";

function OccupancyRateList({
  data,
  isAuthenticated = true,
  controlType,
  saveRecord,
  deleteRecord,
  updateTableState,
  filter,
  user,
  validateRelatedRecord,
  propertyid,
}) {
  const dataSet = "occupancyRates";
  const tableData = data || [];
  const columnHelper = createColumnHelper();
  const [showAlert, setShowAlert] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const tableMeta = {
    updateData: (rowIndex, columnId, value) => {
      updateTableState(dataSet, rowIndex, columnId, value);
    },
    handleSave: async ({ row, column, value }) => {
      const record = {
        ...(tableData[row.index] || {}),
        ...row.original,
        ...(column?.id ? { [column.id]: value } : {}),
      };

      const validation = validateRelatedRecord?.(record, dataSet) || [true, null];
      if (validation[0] !== true) {
        setErrorMessage(validation[1] || "Validation failed");
        setShowAlert(true);
        return { validationFailed: true };
      }

      setShowAlert(false);
      setErrorMessage("");

      try {
        await saveRecord(dataSet, record);
      } catch (error) {
        console.error("Occupancy rate save failed:", error);
        return { saveFailed: true };
      }

      return true;
    },
    handleDelete: async ({ row }) => {
      await deleteRecord(dataSet, row.original);

      const deletedId = row.original.occupancy_rate_id;
      const deletedClientId = row.original._client_id;
      const nextRows = tableData.filter((item) => {
        const matchesSavedId =
          deletedId !== null &&
          deletedId !== undefined &&
          String(item.occupancy_rate_id) === String(deletedId);
        const matchesClientId =
          deletedClientId !== null &&
          deletedClientId !== undefined &&
          item._client_id === deletedClientId;
        return !(matchesSavedId || matchesClientId);
      });

      updateTableState(nextRows, dataSet);
    },
  };

  const columns = [
    columnHelper.accessor("occupancy_rate_id", {
      id: "occupancy_rate_id",
      header: "ID",
      size: 60,
    }),
    columnHelper.accessor("occupancy_percent", {
      id: "occupancy_percent",
      header: "Occupancy Percent",
      cell: EditableCell,
      size: 125,
      meta: { suffix: "%", cellStyle: { textAlign: "right" } },
    }),
    columnHelper.accessor("source", {
      id: "source",
      header: "Source",
      cell: EditableCell,
      size: 205,
    }),
    columnHelper.accessor("source_date", {
      id: "source_date",
      header: "Source Date",
      cell: EditableCell,
      size: 125,
    }),
    columnHelper.display({
      id: "delete_occupancy_rate",
      header: "",
      cell: ({ row }) => (
        <Button
          type="button"
          size="sm"
          variant="danger"
          onClick={async (event) => {
            event.preventDefault();
            const confirmed = window.confirm(
              "Are you sure you want to delete this record?"
            );
            if (confirmed) {
              await tableMeta.handleDelete({ row });
            }
          }}
        >
          Delete
        </Button>
      ),
      size: 80,
      enableSorting: false,
    }),
  ];

  function addRecord() {
    const nextRows = [
      {
        _client_id: `occupancy-rate-${Date.now()}-${tableData.length}`,
        property_id: propertyid,
        occupancy_percent: 100,
        source_date: null,
        created_by: user,
        last_edited_by: user,
        new_record: true,
      },
      ...tableData,
    ];
    updateTableState(nextRows, dataSet);
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="OccupancyRateList">
      {showAlert && <Alert variant="danger">{errorMessage}</Alert>}
      {controlType === "table" && (
        <Row className="show-Container">
          <Col className="text-right">
            <Button variant="secondary" size="sm" onClick={addRecord}>
              Add
            </Button>
          </Col>
        </Row>
      )}
      <Row className="show-Container">
        <Col sm={12} md={12} lg={12}>
          <GenericTable
            columns={columns}
            data={tableData}
            pageSize={10}
            enablePagination={true}
            hasHeaderSorting={true}
            meta={tableMeta}
          />
        </Col>
      </Row>
    </div>
  );
}

export default OccupancyRateList;
